// Client-side mirror of the server's maintenance notice. Listens to
// the WS channels the server fans out:
//
//   maintenance:notice. A future shutdown is scheduled
//   maintenance:cancelled. Admin cleared the schedule
//   maintenance:imminent. Server is going down right now
//
// Components subscribe via onMaintenanceChange() to render a banner
// (scheduled state) or a full-screen overlay (imminent state).

import { on } from './serverClient';
import { getServerUrl } from './updateChannel';

let scheduled = null;
let imminent = null;
// "Update underway" is the post-imminent / post-shutdown phase: the
// server is being redeployed and we want the whole UI to wait for the
// new container to come up before letting players resume.
// updatePhase: 'normal' | 'underway' | 'complete'
//   normal. No update in progress; UI behaves as usual
//   underway. Server is restarting; UI is locked behind an overlay
//   complete. New container detected; show "you can continue" and
//               reload the renderer to re-establish the WS cleanly
let updatePhase = 'normal';
let preUpdateStartedAt = null; // snapshot of last-seen startedAt
let lastSeenStartedAt = null;
let pollTimer = null;
// Did we ever see the server fail to respond while in underway?
// If yes, treat the next *successful* poll as "the new container is
// up" even without a confirmed timestamp diff. Covers the case
// where the player joined too late to have a pre-update snapshot.
let observedDowntimeDuringUpdate = false;
// Hard cap on how long the underway overlay can stay up before
// auto-resolving. If a deploy fails or takes pathologically long the
// player should still be able to recover instead of staring at a
// spinner forever.
let underwayTimeoutTimer = null;
let scheduledEnterTimer = null;
const listeners = new Set();

let bound = false;
let imminentClearTimer = null;

const POLL_INTERVAL_MS = 4000;
const UNDERWAY_MAX_WAIT_MS = 15 * 60 * 1000;

function emit() {
  const payload = { scheduled, imminent, updatePhase, preUpdateStartedAt };
  for (const cb of listeners) {
    try { cb(payload); } catch (err) { console.error('[maintenance] listener error:', err); }
  }
}

// Poll /auth/capacity (already public, already cheap) and read the
// startedAt timestamp the server reports. The endpoint will fail
// while the server is mid-restart, which is the signal we use to
// stay in the underway phase. A successful response with a *changed*
// startedAt tells us the new container is up.
async function pollStartedAt() {
  try {
    const res = await fetch(`${getServerUrl()}/auth/capacity`, { method: 'GET' });
    if (!res.ok) {
      if (updatePhase === 'underway') observedDowntimeDuringUpdate = true;
      return;
    }
    const body = await res.json();
    if (body && typeof body.startedAt === 'string') {
      lastSeenStartedAt = body.startedAt;
      // Two paths into "complete":
      //   1. We had a snapshot from before entering underway and the
      //      timestamp now differs → confirmed redeploy.
      //   2. We saw the server go down at least once during the
      //      underway window and now it answered successfully → very
      //      likely a redeploy (covers the no-snapshot case).
      if (updatePhase === 'underway') {
        const timestampChanged = preUpdateStartedAt && body.startedAt !== preUpdateStartedAt;
        const cameBackFromDown = observedDowntimeDuringUpdate;
        if (timestampChanged || cameBackFromDown) {
          updatePhase = 'complete';
          stopUnderwayTimeout();
          emit();
        }
      }
    }
  } catch (_) {
    // Server is unreachable. That's expected during the restart
    // window. Keep polling; remember we saw downtime so we can flip
    // to complete on the next successful response.
    if (updatePhase === 'underway') observedDowntimeDuringUpdate = true;
  }
}

function startUnderwayTimeout() {
  stopUnderwayTimeout();
  underwayTimeoutTimer = setTimeout(() => {
    if (updatePhase === 'underway') {
      // Safety net: a deploy that doesn't land within 15 minutes is
      // either failed or massively delayed. Drop the lock so the
      // player isn't stuck behind a spinner forever. The next time
      // the server is reachable they'll reconnect normally.
      exitUpdate();
    }
  }, UNDERWAY_MAX_WAIT_MS);
}

function stopUnderwayTimeout() {
  if (underwayTimeoutTimer) { clearTimeout(underwayTimeoutTimer); underwayTimeoutTimer = null; }
}

function startPolling() {
  if (pollTimer) return;
  pollStartedAt();
  pollTimer = setInterval(pollStartedAt, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

function enterUnderway() {
  if (updatePhase === 'underway' || updatePhase === 'complete') return;
  updatePhase = 'underway';
  preUpdateStartedAt = lastSeenStartedAt;
  observedDowntimeDuringUpdate = false;
  emit();
  startPolling();
  startUnderwayTimeout();
}

function exitUpdate() {
  if (updatePhase === 'normal') return;
  updatePhase = 'normal';
  preUpdateStartedAt = null;
  observedDowntimeDuringUpdate = false;
  // Clear any lingering scheduled/imminent state. The maintenance
  // event is over, the new server is up. Without this, the top-of-
  // game banner would re-appear after the underway overlay unmounts,
  // counting down to a time that has already passed.
  scheduled = null;
  imminent = null;
  if (imminentClearTimer) { clearTimeout(imminentClearTimer); imminentClearTimer = null; }
  if (scheduledEnterTimer) { clearTimeout(scheduledEnterTimer); scheduledEnterTimer = null; }
  stopPolling();
  stopUnderwayTimeout();
  emit();
}

function bindOnce() {
  if (bound) return;
  bound = true;
  on('maintenance:notice', (data) => {
    scheduled = data || null;
    emit();
    // Schedule the underway transition for the moment the scheduled
    // time arrives. Not for when SIGTERM hits, which lags the
    // schedule by however long Railway takes to build (3-5 min). This
    // way the lock-overlay timing matches what the admin announced
    // to players: at the scheduled minute, the realm closes for an
    // update, even if Railway is still compiling under the hood.
    if (scheduledEnterTimer) { clearTimeout(scheduledEnterTimer); scheduledEnterTimer = null; }
    if (scheduled?.scheduledAt) {
      const at = new Date(scheduled.scheduledAt).getTime();
      const delay = at - Date.now();
      // Only arm the timer for future scheduled times. If a player
      // joins mid-window the scheduled time is in the past. We
      // don't auto-enter underway in that case because we have no
      // pre-update snapshot to compare against and the server is
      // already responsive on its post-redeploy timestamp.
      if (Number.isFinite(at) && delay > 0) {
        scheduledEnterTimer = setTimeout(enterUnderway, delay);
      }
    }
  });
  on('maintenance:cancelled', () => {
    scheduled = null;
    if (imminentClearTimer) { clearTimeout(imminentClearTimer); imminentClearTimer = null; }
    if (scheduledEnterTimer) { clearTimeout(scheduledEnterTimer); scheduledEnterTimer = null; }
    imminent = null;
    // Cancelling a notice also cancels any in-flight update overlay
    // so admins have a way to recover from a misfire.
    exitUpdate();
    emit();
  });
  on('maintenance:imminent', (data) => {
    imminent = data || { at: new Date(Date.now() + 30_000).toISOString() };
    emit();
    // Imminent fires *just before* the server actually goes down, so
    // we wait for the countdown to expire before locking the UI. The
    // overlay component handles the countdown→underway transition
    // visually; we also schedule a definitive enter at imminent.at +
    // 1s as a safety net in case the WS drops before then.
    const at = new Date(imminent.at).getTime();
    const delay = Math.max(0, at - Date.now() + 1000);
    setTimeout(enterUnderway, delay);
    // Auto-clear the imminent state after its window so a stale
    // overlay doesn't linger if the server somehow comes back without
    // a fresh notice (e.g. the deploy was cancelled mid-flight).
    if (imminentClearTimer) clearTimeout(imminentClearTimer);
    const ms = Math.max(5000, at - Date.now() + 60_000);
    imminentClearTimer = setTimeout(() => { imminent = null; emit(); }, ms);
  });
}

// Called by serverClient (and anything else holding capacity data) so
// the state module always has the most recent startedAt for the
// pre-update snapshot.
export function recordStartedAt(startedAt) {
  if (typeof startedAt === 'string' && startedAt) {
    lastSeenStartedAt = startedAt;
  }
}

// Manually mark the player as having dismissed the "update complete"
// state. Used by the overlay's Continue button. Clears the phase so
// the overlay unmounts; the caller is expected to then reload.
export function acknowledgeUpdateComplete() {
  if (updatePhase === 'complete') {
    exitUpdate();
  }
}

export function initMaintenanceState() {
  bindOnce();
}

export function onMaintenanceChange(callback) {
  bindOnce();
  listeners.add(callback);
  try {
    callback({ scheduled, imminent, updatePhase, preUpdateStartedAt });
  } catch {}
  return () => listeners.delete(callback);
}

export function getMaintenanceState() {
  return { scheduled, imminent, updatePhase, preUpdateStartedAt };
}
